import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { PendingLookup } from '../lib/types';
import type { Session } from '@supabase/supabase-js';

/**
 * Hook to fetch and subscribe to pending_lookups for the current user.
 * Uses Supabase Realtime to update the list when lookups resolve
 * (looking_up → found, failed, timeout).
 *
 * Only returns "active" pending lookups (not confirmed/expired).
 */
export function usePendingLookups(session: Session | null) {
  const [pendingLookups, setPendingLookups] = useState<PendingLookup[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = session?.user?.id;

  // Fetch pending lookups
  const loadPendingLookups = useCallback(async () => {
    if (!userId) {
      setPendingLookups([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('pending_lookups')
      .select('*')
      .eq('user_id', userId)
      .not('status', 'in', '("confirmed","expired")')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to load pending lookups:', error);
    } else {
      setPendingLookups((data ?? []) as PendingLookup[]);
    }
    setLoading(false);
  }, [userId]);

  // Initial load
  useEffect(() => {
    loadPendingLookups();
  }, [loadPendingLookups]);

  // Realtime subscription for updates
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('pending-lookups')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pending_lookups',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Refetch on any change — simpler than merging individual events
          loadPendingLookups();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, loadPendingLookups]);

  // Dismiss a pending lookup (delete it)
  const dismissPending = useCallback(async (id: string) => {
    await supabase.from('pending_lookups').delete().eq('id', id);
    setPendingLookups((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // Mark a pending lookup as confirmed (after user saves via add-booking)
  const confirmPending = useCallback(async (id: string) => {
    await supabase
      .from('pending_lookups')
      .update({ status: 'confirmed', resolved_at: new Date().toISOString() })
      .eq('id', id);
    setPendingLookups((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return {
    pendingLookups,
    loading,
    loadPendingLookups,
    dismissPending,
    confirmPending,
  };
}
